import React from 'react';
import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { camelCase } from 'change-case';
import { useLocation } from '@reach/router';
import { useInstitution } from '#core/Institution';
import { GetRankDataQuery } from '#types/generated/graphql';
import { formatBandedValue } from '#utils/formatUtils';
import { usePageQuery } from '#core/PageQuery';
import { usePermissions } from '#core/Permissions';
import ProductTopbar, {
  ProductTopbarProps,
} from '../../../coreComponents/ProductTopbar/ProductTopbar';
import {
  getWurRankingData,
  wurRankingDataQuery,
} from '../../../coreComponents/ProductTopbar/wurRankingQuery';
import { DataPlusEmbargoProvider } from '../../core/Embargo/components/DataPlusEmbargoProvider';
import { useDataplusEmbargoData } from '../../core/Embargo/contexts/DataplusEmbargoContext';

const DataPlusTopbar: React.FC<ProductTopbarProps> = (props) => {
  const flags = useFlags();
  const WUR_SUBJECTS_DOWNLOAD_ENABLED =
    flags[camelCase('wur-dashboard-subject-download-enabled')];

  const { pathname } = useLocation();

  const {
    institution: { institutionId, name: institutionName },
  } = useInstitution();
  const {
    query: { years, isAfterEmbargoDate },
  } = usePageQuery();

  const {
    availableSelectedYear,
    availableEmbargoYears,
  } = useDataplusEmbargoData();

  const { hasResourcePermissionNew } = usePermissions();

  const queryResult = useQuery<GetRankDataQuery>(
    getWurRankingData,
    wurRankingDataQuery(availableSelectedYear, institutionId),
  );

  const wurBanding = queryResult.data?.release?.level1?.result?.rank;

  const pageName = pathname.slice(pathname.lastIndexOf('/') + 1);

  const action = `dataplus:viewPage:${pageName}`;

  const hasDownloadPermission =
    hasResourcePermissionNew({
      action,
      year: availableSelectedYear,
      institutionId,
    }) && isAfterEmbargoDate[availableSelectedYear];

  const yearsExcludingFirstYear = years.slice(0, -1);

  const topbarConfig = {
    ...props,
    downloadUrls:
      hasDownloadPermission &&
      ((WUR_SUBJECTS_DOWNLOAD_ENABLED &&
        pathname === '/datapoints/wur/subjects') ||
        pathname !== '/datapoints/wur/subjects')
        ? props.downloadUrls
        : undefined,
    years:
      pageName !== 'year-on-year'
        ? availableEmbargoYears
        : yearsExcludingFirstYear,
    title: institutionName,
    titleAcronym: undefined,
  };

  return (
    <ProductTopbar
      {...topbarConfig}
      rank={wurBanding && formatBandedValue(wurBanding)}
    />
  );
};

const DataPlusTopBarEmbargo = (props: ProductTopbarProps) => (
  <DataPlusEmbargoProvider
    viewEmbargoAction="dataplus:viewPage:overview-embargo"
    viewFreeEmbargoAction="dataplus:viewPage:overview-free-embargo"
    isDetailsPage={false}
  >
    <DataPlusTopbar {...props} />
  </DataPlusEmbargoProvider>
);

export default DataPlusTopBarEmbargo;
